import { FC, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';

import CustomButton from '../../common/CustomButton';
import { useUploadAvatarMutation } from '../../store/services/userContactsApi';
import { setUserInfo } from '../../store/slices/userSlice';

import styles from './AvatarUploadModal.module.scss';
import ImageUploader from '../ImageUploader/ImageUploader';

type AvatarUploadModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  isLoading?: boolean;
};

const AvatarUploadModal: FC<AvatarUploadModalProps> = ({
  open,
  onClose,
  onSuccess,
  isLoading: externalLoading = false,
}) => {
  const dispatch = useDispatch();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();

  const isLoading = externalLoading || isUploading;

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
    if (uploadError) {
      setUploadError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage) return;

    try {
      setUploadError(null);

      const response = await uploadAvatar(selectedImage).unwrap();

      dispatch(setUserInfo(response));

      console.log('Avatar uploaded successfully:', response);

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Error uploading avatar:', error);

      const errorMessage =
        error?.data?.message || error?.data?.profile_picture?.[0] || error?.message || 'Failed to upload avatar';

      setUploadError(errorMessage);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedImage(null);
    setUploadError(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="avatar-upload-modal"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box className={styles.modalContainer}>
        <Box className={styles.modalHeader}>
          <Typography
            variant="h5Bold"
            className={styles.modalTitle}
          >
            Update Profile Picture
          </Typography>

          <Box
            className={styles.closeButton}
            onClick={handleClose}
          >
            <CloseIcon sx={{ width: '24px', height: '24px' }} />
          </Box>
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81', textAlign: 'center', mb: 3 }}
          >
            Choose a new profile picture.
          </Typography>

          <ImageUploader
            image={selectedImage}
            onImageChange={handleImageChange}
            disabled={isLoading}
            placeholder={{
              title: 'Select your profile picture',
              subtitle: 'PNG, JPG, GIF up to 5MB',
            }}
          />

          {uploadError && (
            <Typography
              variant="h12"
              sx={{ color: '#C8102E', textAlign: 'center', mt: 2 }}
            >
              {uploadError}
            </Typography>
          )}
        </Box>

        <Box className={styles.modalFooter}>
          <CustomButton
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CustomButton>

          <CustomButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedImage || isLoading}
          >
            {isLoading ? 'Uploading...' : 'Upload Picture'}
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AvatarUploadModal;
